import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  TextField,
  Chip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QRCode from "react-qr-code";
import { useState, useMemo, useEffect } from "react";
import { getBagsCustomers } from "../../../services/saleng";

const BagDialog = ({ open, onClose, data = [], loading, customerUserId }) => {
  const [selectedUid, setSelectedUid] = useState(null);
  const [search, setSearch] = useState("");
  const [bagCustomers, setBagCustomers] = useState({});

  // เลือกตัวแรกอัตโนมัติ
  useEffect(() => {
    if (open) {
      setSelectedUid(data?.[0]?.uid || null);
    }
  }, [open, data]);

  // ค้นหาชื่อเจ้าของถุง
  useEffect(() => {
    if (!open || !data || data.length === 0 || !customerUserId) return;

    // เอา bagCode ทั้งหมด
    const allBagCodes = data.flatMap((item) => item.bags?.map((b) => b.bagCode) || []);
    if (allBagCodes.length === 0) return;

    getBagsCustomers(allBagCodes, customerUserId)
      .then((res) => {
        const items = res?.data?.data || [];
        const bagMap = {};
        items.forEach((item) => {
          bagMap[item.bagCode] = item.customerName;
        });
        setBagCustomers(bagMap);
      })
      .catch(console.error);
  }, [open, data, customerUserId]);

  const currentUid = selectedUid || data?.[0]?.uid;

  const current = data.find((d) => d.uid === currentUid);

  const filteredBags = useMemo(() => {
    if (!search) return current?.bags || [];
    return (current?.bags || []).filter((b) =>
      b.bagCode.toLowerCase().includes(search.toLowerCase()),
    );
  }, [search, current]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* HEADER */}
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        📦 Bag Management
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Typography align="center">กำลังโหลด...</Typography>
        ) : (
          <Grid container spacing={2}>
            {/* LEFT: QR LIST */}
            <Grid item xs={4}>
              <Box
                sx={{
                  height: "60vh",
                  overflowY: "auto",
                  borderRight: "1px solid #eee",
                  pr: 1,
                }}
              >
                <Typography fontWeight="bold" mb={1}>
                  QR ทั้งหมด ({data.length})
                </Typography>

                {data.map((item) => (
                  <Card
                    key={item.uid}
                    onClick={() => setSelectedUid(item.uid)}
                    sx={{
                      mb: 1,
                      p: 1,
                      cursor: "pointer",
                      border:
                        currentUid === item.uid
                          ? "2px solid #1976d2"
                          : "1px solid #eee",
                      bgcolor:
                        currentUid === item.uid
                          ? "rgba(25,118,210,0.05)"
                          : "#fff",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <QRCode value={item.uid} size={40} />
                      <Box>
                        <Typography fontSize={12} noWrap>
                          {item.uid}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${item.bags?.length || 0} ถุง`}
                          color="primary"
                        />
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Grid>

            {/* RIGHT: BAG LIST */}
            <Grid item xs={8}>
              <Box>
                {/* HEADER */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography fontWeight="bold">
                    🧾 Bags ({filteredBags.length})
                  </Typography>

                  <TextField
                    size="small"
                    placeholder="ค้นหา bag..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* BAG GRID */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(120px,1fr))",
                    gap: 1,
                  }}
                >
                  {filteredBags.map((b, i) => (
                    <Card
                      key={i}
                      sx={{
                        p: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                        bgcolor: "#f5f5f5",
                        minHeight: 50,
                        transition: "0.2s",
                        "&:hover": {
                          bgcolor: "#e3f2fd",
                          transform: "scale(1.03)",
                        },
                      }}
                    >
                      <Typography fontWeight="bold" fontSize={14}>
                        {b.bagCode}
                      </Typography>
                      {bagCustomers[b.bagCode] && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            mt: 0.5,
                            lineHeight: 1.2
                          }}
                        >
                          เกษตรกร: {bagCustomers[b.bagCode]}
                        </Typography>
                      )}
                    </Card>
                  ))}
                </Box>

                {/* EMPTY */}
                {filteredBags.length === 0 && (
                  <Typography align="center" color="text.secondary" mt={3}>
                    ไม่พบข้อมูล
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BagDialog;
